import React, { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import { Section } from 'src/components/section';
import { userDataKeys } from 'src/features/details-form/constants';
import { ControlledField } from 'src/components/controlled-field';
import { FieldTypes, TextFieldTypes } from 'src/components/controlled-field/types';
import type { UserDataFormValues } from 'src/features/details-form/types';
import { selectUserLoggedIn } from 'src/store/slices/user-slice';
import { Button } from 'src/components/button';
import { selectIsDualOccupantFlow, selectQueryData } from 'src/store/slices/dual-occupant-slice';
import { LoginDialog } from 'src/features/login-dialog';
import { routes } from 'src/constants/routes';

import { useGetUserExists } from '../hooks/user';

import { styles } from './styles';

import type { EmailFormProps } from './types';

export const EmailForm: FC<EmailFormProps<UserDataFormValues>> = ({
    control,
    onContinue,
    showAdditionalFields,
    showCta,
    isEmailValid,
}) => {
    const { t } = useTranslation();

    const { data: userExists, isLoading } = useGetUserExists();
    const userLoggedIn = useSelector(selectUserLoggedIn);
    const [isLoginDialogOpen, setIsLoginDialogOpen] = useState<boolean>(false);
    const [showPasswordReset, setShowPasswordReset] = useState<boolean>(false);
    const isDualOccupantJourney = useSelector(selectIsDualOccupantFlow);
    const dualOccupantData = useSelector(selectQueryData);

    const disableImmutableFields =
        userLoggedIn || (isDualOccupantJourney && dualOccupantData.userEmail !== null);
    const {
        watch,
        formState: { errors },
    } = useFormContext();

    const email = watch(userDataKeys.email);

    useEffect(() => {
        if (Object.keys(errors).length) {
            const firstErrorField = document.querySelector('.Mui-error');
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [errors]);

    const handleLoginClick = () => {
        setIsLoginDialogOpen(true);
    };

    const handleCloseLoginDialog = () => {
        setIsLoginDialogOpen(false);
    };

    const handleShowPasswordReset = () => {
        setShowPasswordReset(!showPasswordReset);
    };

    if (userExists && userLoggedIn) {
        return null;
    }

    return (
        <Section header={t('your_details_create_an_account')} style={styles.wrapper}>
            <ControlledField
                type={TextFieldTypes.EMAIL}
                name={userDataKeys.email}
                control={control}
                label={t('your_details_form_email_label')}
                placeholder={t('your_details_form_email_placeholder')}
                disabled={disableImmutableFields}
            />
            {showAdditionalFields && (
                <div style={{ display: 'contents' }}>
                    <Typography variant="body4" css={styles.userMessage}>
                        <Trans
                            i18nKey="your_details_form_user_exists_message"
                            components={{
                                span: (
                                    <Typography
                                        component="span"
                                        css={styles.loginLink}
                                        onClick={handleLoginClick}
                                    />
                                ),
                            }}
                        />
                    </Typography>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={userDataKeys.password}
                            control={control}
                            label={t('your_details_form_create_password_label')}
                            placeholder={t('your_details_form_create_password_placeholder')}
                        />
                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={userDataKeys.repeatPassword}
                            control={control}
                            label={t('your_details_form_repeat_password_label')}
                            placeholder={t('your_details_form_repeat_password_placeholder')}
                        />
                    </Box>
                </div>
            )}

            {showCta && (
                <Button
                    variant="contained"
                    css={styles.cta}
                    disabled={!isEmailValid}
                    loading={isLoading}
                    onClick={onContinue}
                >
                    {t('your_details_cta')}
                </Button>
            )}

            <LoginDialog
                open={isLoginDialogOpen}
                closeFn={handleCloseLoginDialog}
                showPasswordReset={showPasswordReset}
                handleShowPasswordReset={handleShowPasswordReset}
                currentEmail={email}
                redirectUrl={`${window.location.origin}${routes.callback.index}`}
            />
        </Section>
    );
};
